/**
 * Section-level Error Boundary for feature-specific error handling
 */

import { Component, ErrorInfo, ReactNode } from 'react'
import { Button } from '@/components/ui/button'

interface SectionErrorBoundaryProps {
  children: ReactNode
  sectionName: string
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

export class SectionErrorBoundary extends Component<SectionErrorBoundaryProps, State> {
  constructor(props: SectionErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error(`Error in section: ${this.props.sectionName}`, error, errorInfo)
  }

  handleReset = (): void => {
    this.setState({ hasError: false, error: null })
  }

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <div className="bg-destructive/10 border border-destructive/50 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-destructive mb-2">
            Lỗi trong {this.props.sectionName}
          </h3>
          <p className="text-destructive mb-3">
            Đã xảy ra lỗi khi tải phần này. Vui lòng thử lại.
          </p>
          <Button onClick={this.handleReset} variant="outline" size="sm" className="min-h-[44px]">
            Thử lại
          </Button>
        </div>
      )
    }

    return this.props.children
  }
}

